import React, { useEffect, useRef, useState } from "react";
import { Segmented, SegmentedOption } from "@axum/ax-design";

// types
import { DateRange } from "../../../types/common";
import { filterGroupClass } from "./Filters.styles";
import { useFilterStatus } from "../../../config/appliedFilters";
import { MAX_SPAN_DAYS } from "../../../config/showRange";

type QuickRange = "hoy" | "7d" | "30d" | "personalizado";

const RANGE_OPTIONS: SegmentedOption<QuickRange>[] = [
  { value: "hoy", label: "Hoy" },
  { value: "7d", label: "7 días" },
  { value: "30d", label: "30 días" },
  { value: "personalizado", label: "Personalizado" },
];

const PRESET_DAYS: Partial<Record<QuickRange, number>> = { hoy: 0, "7d": 6, "30d": 29 };

const pad = (value: number) => String(value).padStart(2, "0");
const toIso = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromIso = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const startOfDay = (date: Date) => addDays(date, 0);

const presetOf = (range: DateRange): QuickRange | null => {
  const today = new Date();
  if (!range.start || !range.end || toIso(range.end) !== toIso(today)) return null;
  const presets = Object.keys(PRESET_DAYS) as QuickRange[];
  return presets.find(preset => toIso(addDays(today, -(PRESET_DAYS[preset] as number))) === toIso(range.start)) || null;
};

interface Props {
  rangeDate: DateRange;
  setRangeDate: (rangeDate: DateRange) => void;
}

const DateFilters = (props: Props) => {
  const { rangeDate } = props;
  const ownRange = useRef<DateRange | null>(null);
  const statusOf = useFilterStatus();
  const [custom, setCustom] = useState(() => presetOf(rangeDate) === null);

  useEffect(() => {
    const setElsewhere = rangeDate !== ownRange.current;
    if (setElsewhere) setCustom(presetOf(rangeDate) === null);
  }, [rangeDate]);

  const setRange = (range: DateRange) => {
    ownRange.current = range;
    props.setRangeDate(range);
  };

  const quick: QuickRange = custom ? "personalizado" : presetOf(rangeDate) || "personalizado";

  const choose = (value: QuickRange) => {
    setCustom(value === "personalizado");
    if (value === "personalizado") return;
    const today = startOfDay(new Date());
    setRange({ start: addDays(today, -(PRESET_DAYS[value] as number)), end: today });
  };

  const changeStart = (value: string) => {
    if (!value) return;
    const start = fromIso(value);
    const end = startOfDay(rangeDate.end);
    const last = addDays(start, MAX_SPAN_DAYS);
    setRange({ start, end: end < start ? start : end > last ? last : end });
  };

  const changeEnd = (value: string) => {
    if (!value) return;
    const end = fromIso(value);
    const start = startOfDay(rangeDate.start);
    const first = addDays(end, -MAX_SPAN_DAYS);
    setRange({ start: start > end ? end : start < first ? first : start, end });
  };

  return (
    <>
      <div className={filterGroupClass(statusOf("rangeDate"))}>
        <span className="ax-filter-group-label" id="o360-range-label">Rango</span>
        <Segmented
          className="ax-segmented-vertical"
          aria-labelledby="o360-range-label"
          options={RANGE_OPTIONS}
          value={quick}
          onChange={choose}
          data-cy="segmented-range"
        />
      </div>
      {quick === "personalizado" && (
        <>
          <div className="ax-filter-group">
            <label className="ax-filter-group-label" htmlFor="input_date_since">Desde</label>
            <input
              id="input_date_since"
              type="date"
              className="ax-form-control"
              required
              value={toIso(rangeDate.start)}
              onChange={e => changeStart(e.target.value)}
              data-cy="input-date-since"
            />
          </div>
          <div className="ax-filter-group">
            <label className="ax-filter-group-label" htmlFor="input_date_until">Hasta</label>
            <input
              id="input_date_until"
              type="date"
              className="ax-form-control"
              required
              min={toIso(rangeDate.start)}
              max={toIso(addDays(rangeDate.start, MAX_SPAN_DAYS))}
              value={toIso(rangeDate.end)}
              onChange={e => changeEnd(e.target.value)}
              data-cy="input-date-until"
            />
          </div>
        </>
      )}
    </>
  );
};

export default DateFilters;
